<template>
  <div id="app">
    <div v-if="routeName === 'cabinet'" id="blast">
      <button @click.prevent="goTop"></button>
      <button @click.prevent="goTop"></button>
    </div>

    <application-header
      v-if="isShownHeader"
      :merchant="MERCHANT_INFO"
    ></application-header>

    <router-view></router-view>

    <capusta-info></capusta-info>
    <capusta-alert></capusta-alert>

    <modal-cabinet-qr-code></modal-cabinet-qr-code>
    <modal-marketplace-categories></modal-marketplace-categories>

    <reports-controller></reports-controller>

    <safe-deal-status-info-controller
      :transaction="SELECTED_TRANSACTION"
    ></safe-deal-status-info-controller>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import { isIOS, isAndroid, isMobile, isMobileOnly } from 'mobile-device-detect';

import { isHeaderVisibility } from 'services/helpers/layout';

import Auth from '@/services/auth';
import WS from '@/services/WebSocket';
import HTTPService from '@/services/Http';

import { SOCKET_INIT } from 'root/.env';

import ApplicationHeader from 'organisms/application/ApplicationHeader';

import ModalCabinetQrCode from '@/components/modals/ModalCabinetQrCode';
import ModalMarketplaceCategories from '@/components/modals/ModalMarketplaceCategories';

import ReportsController from 'organisms/reports/ReportsController';
import SafeDealStatusInfoController from 'organisms/safe-deal/SafeDealStatusInfoController';

import { EVENT_BUS } from '@/eventBus';

export default {
  name: 'Capusta',
  components: {
    ApplicationHeader,
    ModalCabinetQrCode,
    ModalMarketplaceCategories,
    ReportsController,
    SafeDealStatusInfoController,
  },
  created() {
    EVENT_BUS.$on('change.localization', localization => {
      localStorage.setItem('lang', JSON.stringify(localization));
      this.$i18n.locale = localization.code.toLowerCase();
    });

    this.routeName = this.$route.name;

    if (Auth.checkAuth() && this.routeName) {
      this.APP_INIT_LOAD();
    }
  },
  mounted() {
    this.SET_WINDOW_SIZE();

    this.DEVICE_DETECT({
      isIOS,
      isAndroid,
      isMobile,
      isMobileOnly,
    });

    window.addEventListener('resize', this.SET_WINDOW_SIZE);
    window.addEventListener('message', this.receive3dsMessage);
  },
  data() {
    return {
      routeName: '',
    };
  },
  computed: {
    isShownHeader() {
      if (!this.$validate.isObjectWithFields(this.MERCHANT_INFO, ['projects'])) {
        return false;
      }

      return isHeaderVisibility(this.routeName, isMobile);
    },
    ...mapState([
      'MERCHANT_INFO',
      'SELF_EMPLOYED',
      'CARD_BINDING_TOKEN',
      'SELECTED_TRANSACTION',
    ]),
    ...mapGetters([
      'userCanUseCabinet',
      'getLastCardBindingPage',
    ]),
    ...mapGetters('cards', [
      'isEmptyCardList',
    ]),
  },
  watch: {
    '$route'(to) {
      this.routeName = to.name;
    },
    userCanUseCabinet(value) {
      if (value) {
        WS.open(SOCKET_INIT);
      } else {
        WS.close();
      }
    },
  },
  methods: {
    goTop() {
      this.$moveTo().move(document.getElementById('app'));
    },
    receive3dsMessage(message) {
      if (message.data.result === 'wait') {
        HTTPService.getCardStatus(this.CARD_BINDING_TOKEN)
          .then(response => {
            this.REMOVE_CARD_BINDING_TOKEN();

            if (response.data.status === 'SUCCESS') {
              this.$alert({
                type: 'success',
                text: 'card.binding.in.process',
              });

              if (this.getLastCardBindingPage === 'cardBinding') {
                this.$router.push({ name: 'cabinet' });
              } else {
                this.$router.push({
                  name: this.isEmptyCardList ? 'payout' : 'cardSelectedMessage',
                });
              }
            } else {
              this.$alert({
                type: 'warning',
                text: 'card.binding.rejected.by.bank',
              });

              this.$router.push({
                name: this.isEmptyCardList ? 'card.binding' : 'cardSelect',
              });
            }
          })
          .catch(() => {
            this.$alert({
              type: 'warning',
              text: 'failed.contact.the.support',
            });

            this.$router.push({ name: 'payout' });
          });
      } else if (message.data.result === 'fail') {
        this.$alert({
          type: 'warning',
          text: 'failed.contact.the.support',
        });
      }
    },
    resizeMethods(e) {
      this.SET_WINDOW_SIZE(e);

      this.DEVICE_DETECT({
        isIOS,
        isAndroid,
        isMobile,
        isMobileOnly,
      });
    },
    ...mapActions([
      'APP_INIT_LOAD',
    ]),
    ...mapMutations([
      'DEVICE_DETECT',
      'SET_WINDOW_SIZE',
      'REMOVE_CARD_BINDING_TOKEN',
    ]),
  },
};
</script>

<style lang="sass">
  *
    -webkit-locate: auto
    white-space: normal

  #app
    position: relative

  #blast
    &.disabled
      display: none
      visibility: hidden

    &:not(.disabled)
      position: absolute
      top: 0
      right: 0
      left: 0
      height: 100%

      > button
        font-size: 0
        position: absolute
        z-index: 10
        width: 100%
        max-width: calc((100vw - 70rem) / 2)
        height: 100%
        transition: opacity 0.16s ease
        opacity: 0
        background-color: transparent

        &::before
          +pseudo-block

          position: fixed
          bottom: 1.8rem
          width: 1.8rem
          height: 1.8rem
          transform: translate(-50%, -50%)
          opacity: 0.1
          background-image: url("~@/assets/img/icon-arrow-to-top--white.svg")
          background-repeat: no-repeat
          background-position: bottom center
          background-size: contain

        &:hover
          opacity: 1

        &:first-of-type
          left: 0

          #{theme("eclipse")} &
            background-color: transparentize($pure-white, 0.7)

          #{theme("obsidian")} &
            background-color: transparentize(#27283C, 0.9)

        &:last-of-type
          right: 0

          #{theme("eclipse")} &
            background-color: transparentize($pure-white, 0.7)

          #{theme("obsidian")} &
            background-color: transparentize(#27283C, 0.9)

  .fade-enter-active, .fade-leave-active
    transition: opacity .2s ease-in

  .fade-enter, .fade-leave-to
    opacity: 0

  .upload-enter-active, .upload-leave-active
    transition: all .14s ease-in-out
  .upload-enter, .upload-leave-to
    transform: scale(1.5)
    opacity: 0

  .support-enter-active, .support-leave-active
    transition: all .1s ease
  .support-enter, .support-leave-to
    opacity: 0

  .slide-top-enter-active, .slide-top-leave-active
    transition: all .2s ease-in-out
  .slide-top-enter, .slide-top-leave-to
    opacity: 0
  .slide-top-enter
    transform: translateY(30px)
  .slide-top-leave-to
    transform: translateY(-120px)

  .slide-bottom-enter-active, .slide-bottom-leave-active
    transition: all .2s ease-in-out
  .slide-bottom-enter, .slide-bottom-leave-to
    opacity: 0
  .slide-bottom-enter
    transform: translateY(-30px)
  .slide-bottom-leave-to
    transform: translateY(120px)

  .slide-left-enter-active, .slide-left-leave-active
    transition: all .2s ease-in-out
  .slide-left-enter, .slide-left-leave-to
    opacity: 0
  .slide-left-enter
    transform: translateX(30px)
  .slide-left-leave-to
    transform: translateX(-120px)

  .slide-right-enter-active, .slide-right-leave-active
    transition: all .2s ease-in-out
  .slide-right-enter, .slide-right-leave-to
    opacity: .4
  .slide-right-enter
    transform: translateX(-30px)
  .slide-right-leave-to
    transform: translateX(120px)

  .slide-vertical-enter-active, .slide-vertical-leave-active
    transition: all .2s ease
  .slide-vertical-enter, .slide-vertical-leave-to
    transform: translateY(-50px)
    opacity: .4

  .modal-enter-active, .modal-leave-active
    transition: transform .35s ease
  .modal-enter, .modal-leave-to
    transform: translate(-50%, 50%) !important

  .alert-enter-active, .alert-leave-active
    transition: transform .35s ease
  .alert-enter, .alert-leave-to
    transform: translateY(-200px)

  .fromRight-enter-active, .fromRight-leave-active
    transition: all .3s ease
  .fromRight-enter, .fromRight-leave-to
    transform: translateX(200px)
    opacity: 0

  .modal svg
    position: relative
    z-index: 1
    filter: invert(1)
</style>
